import React, { useEffect, useState } from "react";
import api from "../lib/api";
import { Card } from "../components/ui/card";
import { Badge } from "../components/ui/badge";
import { FileText, Plus, Trash2, FolderOpen } from "lucide-react";
import { DOC_TYPES, DOC_CATEGORIES, getDocType } from "../lib/docTypes";
import { getDefaultContent } from "../lib/docTemplates";
import { DocViewerImpl } from "./Documents";
import { toast } from "sonner";

export default function PatientDocuments({ patient, category }) {
  const [items, setItems] = useState([]);
  const [org, setOrg] = useState({});
  const [viewing, setViewing] = useState(null);

  const load = () => {
    if (!patient?.id) return;
    api.get("/documents", { params: { patient_id: patient.id } }).then((r) => setItems(r.data));
  };

  useEffect(() => {
    load();
    api.get("/organization").then((r) => setOrg(r.data));
  }, [patient?.id]);

  const createFromTemplate = (doc_type) => {
    const dt = getDocType(doc_type);
    // Open editor with an in-memory draft (NOT yet persisted to DB).
    // The "下書き作成" button inside the editor will persist it on first click.
    setViewing({
      _new: true,
      doc_type,
      patient_id: patient.id,
      title: dt?.name || "書類",
      content: { ...getDefaultContent(doc_type) },
      status: "draft",
    });
  };

  const persistNew = async (payload) => {
    try {
      const r = await api.post("/documents", {
        doc_type: viewing.doc_type,
        patient_id: viewing.patient_id,
        title: payload.title,
        content: payload.content,
      });
      toast.success(`${getDocType(viewing.doc_type)?.name} の下書きを作成しました`);
      setViewing(r.data);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "作成失敗");
    }
  };

  const update = async (doc, patch) => {
    const r = await api.patch(`/documents/${doc.id}`, patch);
    setViewing(r.data);
    load();
  };

  const del = async (id) => {
    if (!confirm("この書類を削除しますか?")) return;
    await api.delete(`/documents/${id}`);
    if (viewing?.id === id) setViewing(null);
    load();
  };

  // If category is provided, filter to only that category
  const activeCategories = category ? [category] : DOC_CATEGORIES;

  const existingByType = {};
  items.forEach((d) => { (existingByType[d.doc_type] ||= []).push(d); });

  // Filter existing items to active categories
  const filteredItems = category
    ? items.filter((d) => getDocType(d.doc_type)?.category === category)
    : items;

  const itemsByCat = {};
  filteredItems.forEach((d) => {
    const cat = getDocType(d.doc_type)?.category || "その他";
    (itemsByCat[cat] ||= []).push(d);
  });

  return (
    <div className="space-y-6" data-testid="patient-documents-tab">
      {/* Summary header (only when category is filtered) */}
      {category && (
        <Card className="p-4 border-[#E5E5E3] bg-[#D9E2D5]/30">
          <div className="flex items-center justify-between gap-3 flex-wrap">
            <div className="flex items-center gap-3">
              <FileText size={18} className="text-[#2C5545]" />
              <div>
                <div className="font-medium text-[#1C1C1A]">{category}</div>
                <div className="text-xs text-[#4A4A48] font-numeric">
                  {patient?.name_kanji} さん ·
                  テンプレート {DOC_TYPES.filter((d) => d.category === category).length}種類 ·
                  作成済 {filteredItems.length}件
                </div>
              </div>
            </div>
            <div className="text-xs text-[#2C5545] font-numeric tracking-wider flex items-center gap-1 bg-white px-3 py-1.5 rounded-full border border-[#2C5545]/30">
              <Plus size={12} />
              下のテンプレートから選択して新規作成
            </div>
          </div>
        </Card>
      )}

      {/* Templates — grouped by active category */}
      <div className="space-y-6">
        {activeCategories.map((cat) => {
          const catTypes = DOC_TYPES.filter((d) => d.category === cat);
          if (catTypes.length === 0) return null;
          return (
            <div key={cat}>
              <div className="flex items-center gap-3 mb-3">
                <div className="w-1 h-6 bg-[#2C5545] rounded" />
                <h3 className="text-base font-bold text-[#1C1C1A]">
                  {category ? "新規作成テンプレート" : cat}
                </h3>
                <Badge variant="outline" className="border-[#2C5545]/30 text-[#2C5545] font-numeric text-[10px]">
                  <Plus size={10} className="mr-0.5" />
                  {catTypes.length}種
                </Badge>
                <div className="flex-1 border-b border-dashed border-[#E5E5E3]" />
              </div>
              <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-3">
                {catTypes.map((d) => {
                  const existing = existingByType[d.id]?.length || 0;
                  return (
                    <button
                      key={d.id}
                      data-testid={`patient-tmpl-${d.id}`}
                      onClick={() => createFromTemplate(d.id)}
                      className="group relative p-4 text-left border-2 border-dashed border-[#2C5545]/30 rounded-lg bg-white hover:bg-[#D9E2D5]/20 hover:border-[#2C5545] hover:border-solid hover:shadow-md transition"
                    >
                      <div className="flex items-start justify-between mb-2">
                        <div className="w-8 h-8 rounded-lg bg-[#2C5545]/10 flex items-center justify-center group-hover:bg-[#2C5545] transition">
                          <Plus size={16} className="text-[#2C5545] group-hover:text-white transition" strokeWidth={2.5} />
                        </div>
                        {existing > 0 && (
                          <span className="text-[10px] bg-[#2C5545] text-white px-2 py-0.5 rounded-full font-numeric font-medium">
                            {existing}件
                          </span>
                        )}
                      </div>
                      <div className="text-xs font-medium text-[#1C1C1A] line-clamp-2 min-h-[2.4em]">{d.name}</div>
                      <div className="flex items-center gap-1 mt-2 text-[11px] text-[#2C5545] font-medium">
                        <FileText size={11} />
                        <span className="font-numeric tracking-wider">新規作成</span>
                      </div>
                    </button>
                  );
                })}
              </div>
            </div>
          );
        })}
      </div>

      {/* Existing documents */}
      {filteredItems.length > 0 && (
        <div className="pt-4 border-t border-[#E5E5E3]">
          <div className="flex items-center gap-3 mb-4">
            <FolderOpen size={18} className="text-[#2C5545]" />
            <h3 className="text-base font-bold">作成済み書類</h3>
            <Badge variant="outline" className="font-numeric border-[#2C5545] text-[#2C5545]">{filteredItems.length}件</Badge>
          </div>
          <div className="space-y-4">
            {activeCategories.filter((c) => itemsByCat[c]).map((cat) => (
              <div key={cat}>
                {!category && <div className="text-xs font-medium text-[#2C5545] mb-2 tracking-wider">{cat}</div>}
                <Card className="border-[#E5E5E3] bg-white">
                  <div className="divide-y divide-[#E5E5E3]">
                    {itemsByCat[cat].map((d) => (
                      <div key={d.id} className="p-3 flex items-center gap-3 hover:bg-[#F3F3F1]/50 transition">
                        <FileText size={14} className="text-[#2C5545]" />
                        <div className="flex-1 min-w-0">
                          <div className="text-sm font-medium truncate">{d.title}</div>
                          <div className="text-[11px] text-[#8A8A86] font-numeric">
                            {d.created_at?.slice(0, 10)} · v{d.version}
                          </div>
                        </div>
                        <Badge variant="outline" className={d.status === "finalized" ? "border-[#2C5545] text-[#2C5545]" : "text-[#8A8A86]"}>
                          {d.status === "finalized" ? "確定" : "下書き"}
                        </Badge>
                        <button
                          data-testid={`open-doc-${d.id}`}
                          onClick={() => setViewing(d)}
                          className="text-[#2C5545] text-xs hover:underline px-2"
                        >
                          開く
                        </button>
                        <button onClick={() => del(d.id)} className="text-[#A74141] hover:text-[#8B2E2E]">
                          <Trash2 size={13} />
                        </button>
                      </div>
                    ))}
                  </div>
                </Card>
              </div>
            ))}
          </div>
        </div>
      )}

      {viewing && (
        <DocViewerImpl
          doc={viewing}
          patient={patient}
          org={org}
          onClose={() => setViewing(null)}
          onUpdate={(patch) => update(viewing, patch)}
          onCreate={(payload) => persistNew(payload)}
        />
      )}
    </div>
  );
}
